import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { StripeUsageRecord } from '../../../../models/Stripe';
import { useLazyQuery } from '@apollo/client';
import { graphqlSchema } from '../../../../services/graphql.schema';
import Loading from '../../../Animations/loadScreen';

import './AccountConsumptionChart.scss';

interface AccountConsumptionChartProps {
    periodStartsAt: number;
    periodEndsAt: number;
    // For Aspect ratio
    width?: number;
    height?: number;
}

const MARGIN = { top: 24, right: 8, bottom: 32, left: 32 };

function AccountConsumptionChart({
    periodStartsAt,
    periodEndsAt,
    width = 405,
    height = 230
}: AccountConsumptionChartProps) {
    const svgRef = useRef<SVGSVGElement>(null);

    const [consumptionData, setConsumptionData] = useState<StripeUsageRecord[]>([]);
    const [loading, setLoading] = useState(false);

    const [fetchUsageRecords, { loading: queryLoading }] = useLazyQuery(graphqlSchema.STRIPE.PRICES.getUsageByRange, {
        onCompleted: ({ getUsageByRange }: { getUsageByRange: StripeUsageRecord[] }) => {
            console.log('Usage records fetched:', getUsageByRange);
            setConsumptionData(getUsageByRange);
            setLoading(false);
        },
        onError: (error) => {
            console.error('Error fetching usage records:', error);
            setLoading(false);
        }
    })


    useEffect(() => {
        setLoading(true);
        fetchUsageRecords({
            variables: {
                start: periodStartsAt,
                end: periodEndsAt + (1000 * 60 * 60 * 24) // Add one day to include the end date in the query
            }
        });
    }, [periodStartsAt, periodEndsAt, fetchUsageRecords]);

    useEffect(() => {
        if (!svgRef.current || !consumptionData.length) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const innerWidth = width - MARGIN.left - MARGIN.right;
        const innerHeight = height - MARGIN.top - MARGIN.bottom;

        const chart = svg.append('g')
            .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

        // Map records to date-based entries
        const data = consumptionData.map(d => ({
            date: new Date(d.startTime),
            endDate: new Date(d.endTime),
            value: d.aggregatedValue
        }));

        // X scale: band scale using each record's start date
        const xScale = d3.scaleBand<number>()
            .domain(data.map((_, i) => i))
            .range([0, innerWidth])
            .padding(0.25);

        // Y scale: always starts at 0, default max 150 if no data
        const dataMax = d3.max(data, d => d.value) || 0;
        const yMax = dataMax > 0 ? dataMax : 150;
        const GRID_LINES = 7;
        // Round up yMax to a clean integer step for the grid
        const rawStep = yMax / GRID_LINES;
        const step = Math.ceil(rawStep);
        const adjustedMax = step * GRID_LINES;

        const yScale = d3.scaleLinear()
            .domain([0, adjustedMax])
            .range([innerHeight, 0]);

        const gridValues = d3.range(0, GRID_LINES + 1).map(i => i * step);

        // Horizontal grid lines
        chart.append('g')
            .attr('class', 'grid')
            .selectAll('line')
            .data(gridValues)
            .enter()
            .append('line')
            .attr('x1', 0)
            .attr('x2', innerWidth)
            .attr('y1', d => yScale(d))
            .attr('y2', d => yScale(d))
            .attr('stroke', '#E4E4EB')
            .attr('stroke-width', 0.5);

        // Bars (drawn after grid so they render on top)
        chart.selectAll('.bar')
            .data(data)
            .enter()
            .append('rect')
            .attr('class', 'bar')
            .attr('x', (_, i) => xScale(i)!)
            .attr('y', d => yScale(d.value))
            .attr('width', xScale.bandwidth())
            .attr('height', d => (innerHeight - yScale(d.value) || 1)) // Ensure minimum height for visibility
            .attr('fill', '#6fc0e5')
            .attr('rx', 1);

        // X Axis
        const dateFormat = d3.timeFormat('%d-%m');
        const tickInterval = Math.max(1, Math.floor(data.length / 8));
        const xAxis = d3.axisBottom<number>(xScale)
            .tickValues(data.map((_, i) => i).filter((_, i) => i % tickInterval === 0))
            .tickFormat(i => dateFormat(data[i].date))
            .tickSize(0);

        chart.append('g')
            .attr('transform', `translate(0,${innerHeight})`)
            .call(xAxis)
            .call(g => g.select('.domain').remove())
            .selectAll('text')
            .attr('fill', '#a4a5b2')
            .attr('font-size', '9px')
            .attr('dy', '1em');

        // Y Axis labels (integers only)
        const yAxisLabelValues = gridValues.filter((_, i) => i % 2 === 0 || gridValues.length <= 8);

        chart.append('g')
            .selectAll('text')
            .data(yAxisLabelValues)
            .enter()
            .append('text')
            .attr('x', -8)
            .attr('y', d => yScale(d))
            .attr('dy', '0.35em')
            .attr('text-anchor', 'end')
            .attr('fill', '#a4a5b2')
            .attr('font-size', '9px')
            .text(d => Math.round(d));

        // Period boundary line - find where the new period starts (periodEndsAt marks the next period)
        const periodEnd = new Date(periodEndsAt);
        const boundaryIndex = data.findIndex(d => d.date >= periodEnd);

        if (boundaryIndex > 0) {
            const bx = xScale(boundaryIndex)! - (xScale.step() * xScale.padding()) / 2;
            const isRightHalf = bx > innerWidth / 2;
            const textAnchor = isRightHalf ? 'end' : 'start';
            const textOffset = isRightHalf ? -4 : 4;

            chart.append('line')
                .attr('x1', bx)
                .attr('x2', bx)
                .attr('y1', -MARGIN.top + 16)
                .attr('y2', innerHeight)
                .attr('stroke', '#AC2E78')
                .attr('stroke-width', 1.5)
                .attr('stroke-dasharray', '4,3');

            chart.append('text')
                .attr('x', bx + textOffset)
                .attr('y', -MARGIN.top + 12)
                .attr('text-anchor', textAnchor)
                .attr('fill', '#AC2E78')
                .attr('font-size', '9px')
                .attr('font-weight', '500')
                .text('Nuevo periodo');
        }

    }, [consumptionData, width, height, periodEndsAt]);

    return (
        <div className='_chartWrapper'>
            <svg
                ref={svgRef}
                id="AccountConsumptionChart"
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="xMidYMid meet"
                />

            {
                (loading || queryLoading) && (
                    <div className="loading">
                        <Loading
                            display
                            relativePos
                            svgWidth="75px"
                        />
                    </div>
                )
            }
        </div>
    )
}

export default AccountConsumptionChart;
